// Small shared Spark pieces. SparkMyWork and the event Creative tab import these
// by name, so the exports and their props are a contract.

import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import { Button } from '../components/ui/Button';
import Appear from '../components/ui/Appear';
import { TextArea } from '../components/ui/Field';
import { IconCheck } from '../components/ui/icons';
import { FOCUS } from '../components/ui/motion';
import Reveal from '../components/ui/Reveal';
import { Tag } from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import { answerReview } from './sparkApi';
import { PIPELINE, REVIEW_COLOR, SPICE, TASK_STATUS, reviewStateOf } from './sparkRules';
import type { ReviewState, ReviewStep, Stage, TaskStatus } from './sparkRules';
import type { ProductionRow, ReviewMark, ReviewProgress } from './sparkApi';
import { errorMessage, statusOf } from './sparkUi';

export const StageTag: React.FC<{ stage: Stage; className?: string }> = ({ stage, className }) => {
  const p = PIPELINE.find((x) => x.key === stage);
  if (!p) return null;
  return <Tag hex={p.color} className={className}>{p.label}</Tag>;
};

export const StatusTag: React.FC<{ row: Pick<ProductionRow, 'status_reason' | 'archived_at' | 'archived_reason' | 'flown'>; className?: string }> = ({ row, className }) => {
  const s = statusOf(row);
  return s ? <Tag hex={s.color} className={className}>{s.label}</Tag> : null;
};

// Heat climbs from orange to red; Diablo is the hottest red and the only level that glows.
const PIP_ON = ['', 'bg-orange-500', 'bg-orange-500', 'bg-orange-500', 'bg-red-500', 'bg-rose-600'];
const PIP_STEP_MS = 45;
const EMBER = '0 0 6px 0 rgba(244, 63, 94, 0.75), 0 0 12px 1px rgba(251, 113, 133, 0.35)';
// Diablo burns: a flame gradient on each lit pip, each flickering on its own clock so they never
// pulse in step, and three embers drifting off the top.
const FLAME = 'linear-gradient(to top, #b91c1c, #f97316 45%, #fde047)';
const FLAME_MS = [930, 1170, 860, 1290, 1040];
const EMBERS = [{ left: '3px', delay: '0ms' }, { left: '12px', delay: '-700ms' }, { left: '21px', delay: '-1200ms' }];

/** A burning pip's extra class and style at Diablo, for every place pips are drawn. */
export const flamePip = (i: number, burning: boolean): { className: string; style: React.CSSProperties } => (
  burning
    ? {
      className: 'origin-bottom motion-safe:animate-[flame_var(--flame-ms)_ease-in-out_infinite]',
      style: { backgroundImage: FLAME, ['--flame-ms' as string]: `${FLAME_MS[i - 1]}ms`, animationDelay: `${-i * 137}ms`, boxShadow: EMBER },
    }
    : { className: '', style: {} }
);

/**
 * Five pips. A change of level lights (or dims) them one after another in the direction of
 * travel; Diablo glows like an ember. The reading and its reference sentence are a tooltip.
 * `focusable={false}` inside a link or row, where a tab stop per meter is noise.
 */
export const SpiceMeter: React.FC<{ level: number; focusable?: boolean; className?: string }> = ({ level, focusable = true, className = '' }) => {
  const s = SPICE.find((x) => x.level === level);
  const on = Math.max(0, Math.min(5, level));
  // Read during render on purpose: the stagger needs the level the pips were showing before
  // this one, and an effect would run a frame after the transition has already started.
  const last = useRef(on);
  const prior = useRef(on);
  if (last.current !== on) {
    prior.current = last.current;
    last.current = on;
  }
  const was = prior.current;
  const delay = (i: number) => {
    if (on >= was) return i > was && i <= on ? (i - was - 1) * PIP_STEP_MS : 0;
    return i > on && i <= was ? (was - i) * PIP_STEP_MS : 0;
  };
  const label = s ? `Spice ${s.label}: ${s.short}. ${s.reference}.` : 'Spice not set';
  const meter = (
    <span
      tabIndex={focusable ? 0 : undefined}
      role="img"
      aria-label={label}
      className={`relative inline-flex h-[13px] items-end gap-0.5 rounded-inner ${FOCUS} ${className}`}
    >
      {[1, 2, 3, 4, 5].map((i) => {
        const lit = i <= on;
        return (
          <span
            key={i}
            aria-hidden="true"
            className={`w-[5px] rounded-inner transition-[background-color,box-shadow] duration-standard ease-emphasized ${
              lit ? PIP_ON[on] : 'bg-surface-container-highest'
            } ${i === 5 ? 'h-[13px]' : 'h-[10px]'} ${flamePip(i, on === 5 && lit).className}`}
            style={{ transitionDelay: `${delay(i)}ms`, boxShadow: 'none', ...flamePip(i, on === 5 && lit).style }}
          />
        );
      })}
      {on === 5 && EMBERS.map((e) => (
        <span
          key={e.left}
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[9px] hidden h-[3px] w-[3px] rounded-full bg-[#fde047] motion-safe:block motion-safe:animate-[emberRise_1.6s_ease-out_infinite]"
          style={{ left: e.left, animationDelay: e.delay, boxShadow: '0 0 4px #f97316' }}
        />
      ))}
    </span>
  );
  if (!s) return meter;
  return <Tooltip label={`${s.label}: ${s.short}`} note={s.reference}>{meter}</Tooltip>;
};

// ── Review Status ────────────────────────────────────────────────────────────

const STEP_WORD: Record<ReviewStep, string> = {
  designer: 'Designer', director: 'Director', producer: 'Producer', client: 'Client', pilot: 'Pilot',
};
const MARK_WORD: Record<ReviewMark, string> = { pass: 'passed', now: 'waiting', changes: 'asked for changes', next: 'not yet' };

const stepWord = (s: ReviewProgress['steps'][number]) => (s.step === 'designer' && s.merged ? 'Designer and director' : STEP_WORD[s.step]);

/** "Designer passed, Director waiting, Producer not yet, Client not yet". */
export const reviewWords = (steps: ReviewProgress['steps']): string =>
  steps.map((s) => `${stepWord(s)} ${MARK_WORD[s.mark]}`).join(', ');

// The Review order's marks (SparkReviewPanel) at row size: green passed, gold lit waiting, orange
// sent back, and a recessed well for a step still to come.
const MARK_FILL: Record<ReviewMark, string> = {
  pass: 'bg-green-400/15',
  now: 'bg-primary/15',
  changes: 'bg-orange-400/15',
  next: 'bg-surface-container-high',
};
const LAYER = 'absolute inset-0 rounded-control transition-opacity duration-standard ease-emphasized';
const GLYPH = 'absolute inset-0 grid place-items-center transition-[opacity,transform] duration-standard ease-emphasized motion-reduce:scale-100';

/** Sent back: the work returns to whoever made it. */
const IconSentBack: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="5" y="10.5" width="15" height="3" rx="1.5" fill="currentColor" />
    <rect x="4" y="10.5" width="10" height="3" rx="1.5" transform="rotate(-45 5.5 12)" fill="currentColor" />
    <rect x="4" y="10.5" width="10" height="3" rx="1.5" transform="rotate(45 5.5 12)" fill="currentColor" />
  </svg>
);

const ReviewMarkBox: React.FC<{ mark: ReviewMark }> = ({ mark }) => {
  // Counted during render so the remount below replays the draw each time a pass lands; a mark
  // already passed on load holds still, or every row would draw its checks on arrival.
  const lands = useRef(0);
  const was = useRef(mark);
  if (was.current !== mark) {
    was.current = mark;
    if (mark === 'pass') lands.current += 1;
  }
  return (
    <span className={`relative h-[18px] w-[18px] flex-none rounded-control transition-[background-color] duration-standard ease-emphasized ${MARK_FILL[mark]}`}>
      {/* The recess is inset and the glow is not, and the two cannot interpolate, so each fades as a layer. */}
      <span className={`${LAYER} shadow-recess ${mark === 'next' ? 'opacity-100' : 'opacity-0'}`} />
      <span className={`${LAYER} shadow-glow ${mark === 'now' ? 'opacity-100' : 'opacity-0'}`} />
      <span className={`${GLYPH} text-green-300 ${mark === 'pass' ? 'scale-100 opacity-100' : 'scale-75 opacity-0'} ${lands.current ? '' : '[&_path]:!animate-none'}`}>
        <IconCheck key={lands.current} draw className="h-[11px] w-[11px]" />
      </span>
      <span className={`${GLYPH} text-orange-300 ${mark === 'changes' ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`}>
        <IconSentBack className="h-[11px] w-[11px]" />
      </span>
    </span>
  );
};

/** The line from one step to the next fills green once the step before it passes. */
const ReviewJoint: React.FC<{ lit: boolean }> = ({ lit }) => (
  <span className="h-0.5 w-2 flex-none overflow-hidden rounded-inner bg-outline-variant">
    <span
      className={`block h-full origin-left bg-green-300/70 transition-[transform,opacity] duration-spring ease-spring motion-reduce:scale-x-100 motion-reduce:duration-standard ${
        lit ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
      }`}
    />
  </span>
);

/**
 * A project's review chain as one mark per step. The tooltip carries Review Status in words, the
 * deliverable shown and how many more wait. A project with nothing sent reads "Not sent" in the
 * same slot, cross-fading with the strip, unless `chainWhenUnsent` draws its chain to come.
 * `focusable={false}` inside a link or row, where a tab stop per strip is noise.
 */
export const ReviewStrip: React.FC<{
  review: ReviewProgress;
  focusable?: boolean;
  chainWhenUnsent?: boolean;
  /** The edge "Not sent" keeps to in the strip's slot. */
  align?: 'start' | 'end';
  className?: string;
}> = ({ review, focusable = true, chainWhenUnsent = false, align = 'start', className = '' }) => {
  const chain = (review.state !== 'not_sent' || chainWhenUnsent) && review.steps.length > 0;
  const words = reviewWords(review.steps);
  const more = review.more > 0 ? `+${review.more} more in review` : '';
  return (
    <span className={`inline-flex min-w-0 items-center ${className}`}>
      <span className={`grid flex-none grid-cols-1 grid-rows-1 items-center ${align === 'end' ? 'justify-items-end' : 'justify-items-start'}`}>
        <span
          aria-hidden={chain || undefined}
          className={`col-start-1 row-start-1 whitespace-nowrap text-xs text-on-surface-variant transition-opacity duration-standard ease-emphasized ${chain ? 'opacity-0' : 'opacity-100'}`}
        >
          Not sent
        </span>
        <Tooltip label={chain ? reviewStateOf(review.state).label : ''} note={[review.task, words, more].filter(Boolean).join('\n')}>
          <span
            role="img"
            aria-label={`Review${review.task ? ` of ${review.task}` : ''}: ${words}`}
            aria-hidden={chain ? undefined : true}
            tabIndex={focusable && chain ? 0 : undefined}
            className={`col-start-1 row-start-1 inline-flex items-center rounded-inner transition-opacity duration-standard ease-emphasized ${FOCUS} ${chain ? 'opacity-100' : 'opacity-0'}`}
          >
            {review.steps.map((s, i) => (
              <React.Fragment key={s.step}>
                {i > 0 && <ReviewJoint lit={review.steps[i - 1].mark === 'pass'} />}
                <ReviewMarkBox mark={s.mark} />
              </React.Fragment>
            ))}
          </span>
        </Tooltip>
      </span>
      <Appear show={review.more > 0} className="ml-1.5 whitespace-nowrap text-xs tabular-nums text-on-surface-variant">+{review.more} more</Appear>
    </span>
  );
};

const STATE_TONE: Record<ReviewState, string> = {
  not_sent: 'text-on-surface-variant',
  designer: 'text-primary',
  director: 'text-primary',
  producer: 'text-primary',
  client: 'text-primary',
  changes: 'text-orange-300',
  reviewed: 'text-green-300',
};

/** Review Status in words, in its state's colour. New words fade in over the old. */
export const ReviewStatusText: React.FC<{ state: ReviewState; className?: string }> = ({ state, className = '' }) => {
  // Fades only after a change: words already there on load hold still.
  const swapped = useRef(false);
  const last = useRef(state);
  if (last.current !== state) {
    last.current = state;
    swapped.current = true;
  }
  return (
    <span
      key={state}
      className={`min-w-0 truncate text-sm font-medium ${STATE_TONE[state]} ${swapped.current ? 'animate-[showup_200ms_cubic-bezier(0.2,0,0,1)_both]' : ''} ${className}`}
    >
      {reviewStateOf(state).label}
    </span>
  );
};

export const TaskStatusTag: React.FC<{ status: TaskStatus; className?: string }> = ({ status, className }) => {
  const t = TASK_STATUS[status];
  if (!t) return null;
  return <Tag hex={t.color} className={className}>{t.label}</Tag>;
};

interface ReviewRequest {
  id: string;
  kind: 'chain' | 'pilot';
  step: ReviewStep;
  merged: boolean;
  ask: string | null;
  task_name: string;
  production_name: string;
  production_id: string;
  requested_by_name?: string | null;
}

export const ReviewRequestCard: React.FC<{ review: ReviewRequest; onDone: () => void }> = ({ review, onDone }) => {
  const toast = useToast();
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (action: 'pass' | 'changes') => {
    if (busy) return;
    setBusy(true);
    try {
      await answerReview(review.id, action, action === 'changes' ? note.trim() : undefined);
      onDone();
    } catch (e) {
      toast.error(errorMessage(e, 'Could not send your answer. Try again.'));
      setBusy(false);
    }
  };

  const isPilot = review.kind === 'pilot';
  const tagLabel = isPilot ? 'Pilot asked' : reviewStateOf(review.step as ReviewState).label;
  const who = review.requested_by_name || 'The Studio';
  let body: string;
  if (isPilot) body = 'This is a question from the Studio. The review order does not wait on your answer.';
  else if (review.step === 'client') body = "The producer is recording the client's answer. Pass it if the client approved, or ask for changes.";
  else if (review.merged) body = 'You are both designer and director here, so this one answer covers both reviews.';
  else body = 'Look it over and pass it on, or send it back with what to change.';

  return (
    <div className="rounded-panel bg-surface-container p-3.5">
      <Tag hex={REVIEW_COLOR}>{tagLabel}</Tag>
      <p className="mt-2 text-sm text-on-surface">
        <Link
          to={`/spark/projects/${review.production_id}`}
          className={`rounded-inner font-semibold text-on-surface hover:text-primary transition-colors duration-quick ease-emphasized ${FOCUS}`}
        >
          {review.production_name}
        </Link>
        : {review.task_name}
      </p>
      <p className="mt-0.5 text-xs text-on-surface-variant">{who} asked.</p>
      {review.ask && <p className="mt-2 rounded-control bg-surface-container-high px-2.5 py-1.5 text-sm text-on-surface">{review.ask}</p>}
      <p className="mt-2 text-sm text-on-surface-variant">{body}</p>

      <Reveal show={asking}>
        <div className="px-px pb-px pt-2.5">
          <label htmlFor={`rr-note-${review.id}`} className="sr-only">Note for {who}, optional</label>
          <TextArea
            id={`rr-note-${review.id}`}
            autoFocus
            rows={2}
            value={note}
            maxLength={2000}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What should change? Optional."
          />
        </div>
      </Reveal>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {asking ? (
          <>
            <Tooltip label="Keep the review open without answering">
              <Button variant="tonal" disabled={busy} onClick={() => setAsking(false)}>Cancel</Button>
            </Tooltip>
            <Tooltip label={`Send it back to ${who === 'The Studio' ? 'the Studio' : who} with your note`}>
              <Button variant="filled" disabled={busy} onClick={() => send('changes')}>Send changes</Button>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip label="Write what should change before sending it back">
              <Button variant="tonal" disabled={busy} onClick={() => setAsking(true)}>Ask for changes</Button>
            </Tooltip>
            <Tooltip label={isPilot ? 'Answer the Studio: it looks good' : 'Pass it on to the next review'}>
              <Button variant="filled" disabled={busy} onClick={() => send('pass')} leading={<IconCheck className="h-3.5 w-3.5" />}>
                Pass
              </Button>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};
